<template>
  <div class="config-container">
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>加载配置中...</p>
      </div>
    </div>

    <div class="page-head">
      <div>
        <h2 class="page-title">{{ isEditMode ? '编辑配置 - 冒险岛枫星' : '创建配置 - 冒险岛枫星' }}</h2>
        <p class="page-subtitle">挂机地图、杂货铺/买药、辅助选项与自动释放技能。</p>
      </div>
    </div>

    <div class="config-card">
      <div class="config-form">
        <div class="form-item">
          <label class="form-label">配置名称</label>
          <input
            v-model="configForm.name"
            class="native-input"
            type="text"
            placeholder="输入配置名称"
            :disabled="isEditMode && isDefaultConfig"
            :title="isEditMode && isDefaultConfig ? '默认配置不允许修改名称' : ''"
          />
        </div>

        <div class="form-item">
          <label class="form-label">配置描述</label>
          <input
            v-model="configForm.description"
            class="native-input"
            type="text"
            placeholder="输入配置描述（可选）"
            :disabled="isViewMode || (isEditMode && isDefaultConfig)"
          />
        </div>

        <div class="section-title">挂机设置</div>
        <div class="form-item form-item--one-line form-item--hang-map">
          <label class="form-label form-label--inline" for="hang-map-select">挂机地图</label>
          <div class="hang-map-field-body">
            <el-select
              id="hang-map-select"
              v-model="selectedHangMap"
              class="hang-map-el-select"
              filterable
              clearable
              :filter-method="setHangMapFilterQuery"
              placeholder="输入关键字筛选或下拉选择"
              :disabled="isViewMode || (isEditMode && isDefaultConfig)"
              popper-class="hang-map-select-popper"
              fit-input-width
              no-match-text="没有匹配的地图，换个关键词试试"
              no-data-text="暂无地图数据"
              teleported
              @visible-change="onHangMapDropdownVisible"
            >
              <el-option
                v-for="row in filteredHangMapRowsForSelect"
                :key="row.id != null ? row.id : row.optionValue"
                :label="row.optionValue"
                :value="row.optionValue"
              />
              <template #empty>
                <div class="hang-map-dropdown-empty">
                  暂无选项：请在库表 adventure_hang_map_options 中添加（见 backend/sql）
                </div>
              </template>
            </el-select>
          </div>
        </div>

        <div class="section-title">自动杂货与战斗辅助</div>

        <div
          class="form-item form-item--grocery"
          :class="{ 'form-item--grocery-unsupported-map': !hangMapSupportsGrocery }"
        >
          <div class="grocery-master-row">
            <label class="form-check" @click.capture="onGroceryMasterClick">
              <input
                v-model="adventureExtra.自动杂货铺"
                type="checkbox"
                class="native-checkbox"
                :disabled="groceryMasterDisabled"
              />
              <span class="grocery-master-label">自动杂货铺</span>
              <span class="form-tag">主开关</span>
            </label>
          </div>
          <p v-if="!hangMapSupportsGrocery" class="form-hint form-hint--warn">
            当前挂机地图不支持自动杂货铺；已开放地图：{{ grocerySupportedMapsLabel }}。请切换挂机地图或等待后续开放。
          </p>
          <p v-else class="form-hint">
            提示：只有文档带 🤖 的地图才有自动寻路买药功能，更多地图正在加速开发中：
            <a href="https://www.yuque.com/liuce1996/yunhu" target="_blank" rel="noopener noreferrer">语雀文档</a>
          </p>

          <div
            class="grocery-sub"
            :class="{ 'grocery-sub--locked': !adventureExtra.自动杂货铺 || !hangMapSupportsGrocery }"
          >
            <div class="check-row check-row--buy">
              <label class="form-check">
                <input
                  v-model="adventureExtra.自动买药"
                  type="checkbox"
                  class="native-checkbox"
                  :disabled="grocerySubDisabled"
                />
                <span>自动买药</span>
              </label>
              <select
                v-model="adventureExtra.自动买药类型"
                class="native-select native-select--medicine"
                :disabled="grocerySubDisabled || !adventureExtra.自动买药"
              >
                <option value="白药水">白药水</option>
                <option value="烤鳗鱼">烤鳗鱼</option>
                <option value="烤鳗鱼">棒棒冰</option>

              </select>
            </div>
            <p class="form-hint grocery-sub-hint">需自行装配宠物，并把药品放在消耗栏对应位置。</p>

            <div class="check-row check-row--3">
              <label class="form-check form-check--cell">
                <input
                  v-model="adventureExtra.自动卖装备栏"
                  type="checkbox"
                  class="native-checkbox"
                  :disabled="grocerySubDisabled"
                />
                <span>自动卖装备栏</span>
              </label>
              <label class="form-check form-check--cell">
                <input
                  v-model="adventureExtra.自动卖其他栏"
                  type="checkbox"
                  class="native-checkbox"
                  :disabled="grocerySubDisabled"
                />
                <span>自动卖其他栏</span>
              </label>
              <label class="form-check form-check--cell">
                <input
                  v-model="adventureExtra.自动卖消耗品"
                  type="checkbox"
                  class="native-checkbox"
                  :disabled="grocerySubDisabled"
                />
                <span>自动卖消耗品</span>
              </label>
            </div>
            <p class="form-hint form-hint--warn grocery-sub-hint">
              自动出售相关功能可能存在风险；若出现问题请自行负责，建议运行稳定一段时间后再开启。
            </p>
          </div>

          <div class="medicine-interval-row medicine-interval-row--standalone medicine-interval-row--one-line">
            <label class="medicine-interval-label" for="medicine-interval-input">买药定时</label>
            <input
              id="medicine-interval-input"
              v-model.number="adventureExtra.自动买药间隔分钟"
              class="native-input medicine-interval-input"
              type="number"
              :min="medBuyIntervalMin"
              :max="medBuyIntervalMax"
              step="1"
              inputmode="numeric"
              :disabled="extraFieldsDisabled"
            />
            <span class="medicine-interval-unit">分钟</span>
            <span class="medicine-interval-hint-inline">
              默认 {{ medBuyIntervalDefault }} 分钟，{{ medBuyIntervalMin }}～{{ medBuyIntervalMax }}；
            </span>
          </div>
        </div>

        <div class="check-row check-row--3 form-item--tight">
          <label class="form-check form-check--cell">
            <input
              v-model="adventureExtra.统计金币"
              type="checkbox"
              class="native-checkbox"
              :disabled="extraFieldsDisabled"
            />
            <span>统计金币</span>
          </label>
          <label class="form-check form-check--cell">
            <input
              v-model="adventureExtra.死亡自动回图"
              type="checkbox"
              class="native-checkbox"
              :disabled="extraFieldsDisabled"
            />
            <span>死亡自动回图</span>
          </label>
          <label class="form-check form-check--cell">
            <input
              v-model="adventureExtra.掉线重登录"
              type="checkbox"
              class="native-checkbox"
              :disabled="extraFieldsDisabled"
            />
            <span>掉线重登录</span>
          </label>
        </div>
        <div class="check-row check-row--script-auto-update form-item--tight">
          <label class="form-check form-check--cell">
            <input
              v-model="adventureExtra.脚本自动更新最高版本"
              type="checkbox"
              class="native-checkbox"
              :disabled="extraFieldsDisabled"
            />
            <span>脚本自动更新最高版本</span>
          </label>
        </div>

        <div class="form-item">
          <div class="form-inline">
            <label class="form-check">
              <input
                v-model="adventureExtra.自动代理"
                type="checkbox"
                class="native-checkbox"
                :disabled="extraFieldsDisabled"
              />
              <span>自动代理</span>
            </label>
            <select
              v-model="adventureExtra.代理类型"
              class="native-select native-select--inline"
              :disabled="extraFieldsDisabled || !adventureExtra.自动代理"
            >
              <option value="kit">kit</option>
              <option value="nekobox">nekobox</option>
            </select>
          </div>
        </div>

        <div class="form-item form-item--one-line">
          <div class="offline-one-line">
            <label class="form-check">
              <input
                v-model="adventureExtra.定时离线.启用"
                type="checkbox"
                class="native-checkbox"
                :disabled="extraFieldsDisabled"
              />
              <span>定时离线</span>
            </label>
            <span class="offline-one-line__seg">时间段（同一天内，不跨天）</span>
            <select
              v-model.number="adventureExtra.定时离线.开始小时"
              class="native-select native-select--hour"
              :disabled="extraFieldsDisabled || !adventureExtra.定时离线.启用"
            >
              <option v-for="h in hourOptions" :key="'s-' + h" :value="h">{{ formatHour(h) }}</option>
            </select>
            <span class="offline-hours-sep">至</span>
            <select
              v-model.number="adventureExtra.定时离线.结束小时"
              class="native-select native-select--hour"
              :disabled="extraFieldsDisabled || !adventureExtra.定时离线.启用"
            >
              <option v-for="h in hourOptions" :key="'e-' + h" :value="h">{{ formatHour(h) }}</option>
            </select>
            <span class="offline-hours-unit">点（0～23）</span>
          </div>
        </div>

        <div class="form-item form-item--one-line form-item--job-row">
          <label class="form-label form-label--inline">职业</label>
          <select
            v-model="adventureExtra.职业"
            class="native-select native-select--job-inline"
            :disabled="extraFieldsDisabled"
          >
            <option value="阴阳师">阴阳师</option>
            <option value="恶魔">恶魔</option>
            <option value="其他">其他</option>
          </select>
        </div>

        <div class="section-title">自动释放技能</div>
        <div class="skill-panel" :class="{ 'skill-panel--disabled': skillFieldsDisabled }">
          <p class="skill-panel-desc">
            默认关闭；开启后填写 <strong>5～9999</strong> 秒，最短 ≤ 最长，区间内随机释放。关闭时使用默认 <strong>60～90</strong> 秒写入配置。
          </p>
          <div class="skill-rows">
            <div
              v-for="key in skillKeys"
              :key="key"
              class="skill-line"
            >
              <label
                class="skill-switch"
                :class="{ 'skill-switch--disabled': skillFieldsDisabled }"
              >
                <input
                  v-model="skillIntervals[key].enabled"
                  type="checkbox"
                  class="skill-switch-input"
                  :disabled="skillFieldsDisabled"
                  :aria-label="`${key} 键自动释放`"
                />
                <span class="skill-switch-track" aria-hidden="true">
                  <span class="skill-switch-knob"></span>
                </span>
                <span class="skill-switch-caption" aria-hidden="true">{{ skillIntervals[key].enabled ? 'ON' : 'OFF' }}</span>
              </label>
              <span class="skill-line-key">{{ key }} 键</span>
              <div class="skill-range-row">
                <input
                  v-model.number="skillIntervals[key].minSec"
                  class="skill-num-input"
                  type="number"
                  min="5"
                  max="9999"
                  step="1"
                  inputmode="numeric"
                  :disabled="skillFieldsDisabled || !skillIntervals[key].enabled"
                  :aria-label="`${key} 键最短秒数`"
                />
                <span class="skill-range-tilde" aria-hidden="true">～</span>
                <input
                  v-model.number="skillIntervals[key].maxSec"
                  class="skill-num-input"
                  type="number"
                  min="5"
                  max="9999"
                  step="1"
                  inputmode="numeric"
                  :disabled="skillFieldsDisabled || !skillIntervals[key].enabled"
                  :aria-label="`${key} 键最长秒数`"
                />
                <span class="skill-range-suffix">秒</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isViewMode" class="bottom-actions">
      <button
        class="primary-btn"
        :disabled="saving"
        @click="saveConfig"
      >
        {{ saving ? '保存中...' : (isEditMode ? '更新配置' : '保存配置') }}
      </button>
    </div>
    <div v-else class="view-mode-hint">
      当前为只读查看模式
    </div>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getConfig, createConfig, updateConfig } from '@/api/configs'
import { getAdventureHangMapOptions } from '@/api/adventureHangMaps'

const GAME_NAME = '冒险岛枫星'

/**
 * 支持「自动杂货铺」的挂机地图前缀（与 adventure_hang_map_options.option_value 匹配）。
 * - 与某项完全一致，或以该项为前缀（如「时尚大道」匹配「时尚大道 10-20级」）。
 * 后续开放：追加名称字符串即可。
 */
const GROCERY_SUPPORTED_HANG_MAPS = ['时尚大道']

function isHangMapGrocerySupported(mapValue) {
  const s = mapValue != null ? String(mapValue).trim() : ''
  if (!s) return false
  return GROCERY_SUPPORTED_HANG_MAPS.some((allowed) => {
    const a = String(allowed).trim()
    if (!a) return false
    return s === a || s.startsWith(a)
  })
}

/** 当前地图不支持杂货铺时，关掉主开关并清空子勾选（避免置灰后仍显示已勾选） */
function clearGroceryChainIfMapUnsupported(mapValue, adventureExtraTarget) {
  if (!adventureExtraTarget || typeof adventureExtraTarget !== 'object') return
  if (isHangMapGrocerySupported(mapValue)) return
  adventureExtraTarget.自动杂货铺 = false
  adventureExtraTarget.自动买药 = false
  adventureExtraTarget.自动卖装备栏 = false
  adventureExtraTarget.自动卖其他栏 = false
  adventureExtraTarget.自动卖消耗品 = false
}

const GROCERY_SUPPORTED_MAPS_LABEL = GROCERY_SUPPORTED_HANG_MAPS.join('、')

/** 挂机地图搜索：整句包含、空格分词且匹配、或字符按序「模糊」匹配 */
function hangMapSearchMatch(queryRaw, optionText) {
  const raw = String(queryRaw ?? '').trim().toLowerCase()
  if (!raw) return true
  const text = String(optionText ?? '').toLowerCase()
  if (text.includes(raw)) return true
  const tokens = raw.split(/\s+/).filter(Boolean)
  return tokens.every((tok) => {
    if (!tok) return true
    if (text.includes(tok)) return true
    let ti = 0
    for (let i = 0; i < text.length && ti < tok.length; i++) {
      if (text[i] === tok[ti]) ti++
    }
    return ti === tok.length
  })
}

const SKILL_KEYS = ['A', 'B', 'C', 'D']
const SEC_MIN = 5
const SEC_MAX = 9999
const DEFAULT_SKILL_MIN = 60
const DEFAULT_SKILL_MAX = 90

function defaultSkillIntervals() {
  const o = {}
  SKILL_KEYS.forEach((k) => {
    o[k] = {
      enabled: false,
      minSec: DEFAULT_SKILL_MIN,
      maxSec: DEFAULT_SKILL_MAX
    }
  })
  return o
}

function parseEnabled(raw) {
  if (raw === true || raw === 1 || raw === '1' || raw === 'true') return true
  return false
}

function normalizeSec(n) {
  const v = Math.floor(Number(n))
  if (!Number.isFinite(v)) return null
  return v
}

function parseLoadedSkillIntervals(raw) {
  const out = defaultSkillIntervals()
  if (!raw || typeof raw !== 'object') return out
  SKILL_KEYS.forEach((k) => {
    const v = raw[k]
    if (Array.isArray(v) && v.length >= 2) {
      const a = normalizeSec(v[0])
      const b = normalizeSec(v[1])
      if (a != null && b != null) {
        out[k] = { enabled: false, minSec: a, maxSec: b }
      }
    } else if (v && typeof v === 'object' && !Array.isArray(v) && v.minSec != null && v.maxSec != null) {
      const a = normalizeSec(v.minSec)
      const b = normalizeSec(v.maxSec)
      if (a != null && b != null) {
        out[k] = {
          enabled: parseEnabled(v.enabled),
          minSec: a,
          maxSec: b
        }
      }
    }
  })
  return out
}

const JOB_OPTIONS = ['阴阳师', '恶魔', '其他']
const MEDICINE_TYPES = ['白药水', '烤鳗鱼', '棒棒冰']
const PROXY_TYPES = ['kit', 'nekobox']

/** 自动买药定时间隔（分钟） */
const MED_BUY_INTERVAL_MIN = 60
const MED_BUY_INTERVAL_MAX = 2880
const MED_BUY_INTERVAL_DEFAULT = 240

function normalizeMedicineBuyIntervalMin(n) {
  const v = Math.round(Number(n))
  if (!Number.isFinite(v)) return null
  return v
}

function clampMedicineBuyIntervalMin(n) {
  const v = normalizeMedicineBuyIntervalMin(n)
  if (v == null) return MED_BUY_INTERVAL_DEFAULT
  return Math.min(MED_BUY_INTERVAL_MAX, Math.max(MED_BUY_INTERVAL_MIN, v))
}

function clampHour(n) {
  const v = Math.floor(Number(n))
  if (!Number.isFinite(v)) return 0
  return Math.min(23, Math.max(0, v))
}

function defaultAdventureExtra() {
  return {
    自动杂货铺: false,
    自动买药: false,
    自动买药类型: '白药水',
    自动买药间隔分钟: MED_BUY_INTERVAL_DEFAULT,
    自动卖装备栏: false,
    自动卖其他栏: false,
    自动卖消耗品: false,
    统计金币: true,
    死亡自动回图: false,
    掉线重登录: false,
    脚本自动更新最高版本: false,
    自动代理: false,
    代理类型: 'kit',
    定时离线: {
      启用: false,
      开始小时: 0,
      结束小时: 23
    },
    职业: '阴阳师'
  }
}

function parseAdventureExtra(raw) {
  const d = defaultAdventureExtra()
  if (!raw || typeof raw !== 'object') return d
  if (raw.自动杂货铺 !== undefined) d.自动杂货铺 = parseEnabled(raw.自动杂货铺)
  if (raw.自动买药 !== undefined) d.自动买药 = parseEnabled(raw.自动买药)
  if (MEDICINE_TYPES.includes(raw.自动买药类型)) d.自动买药类型 = raw.自动买药类型
  if (raw.自动买药间隔分钟 !== undefined) {
    const iv = normalizeMedicineBuyIntervalMin(raw.自动买药间隔分钟)
    d.自动买药间隔分钟 = iv != null ? clampMedicineBuyIntervalMin(iv) : MED_BUY_INTERVAL_DEFAULT
  }
  if (raw.自动卖装备栏 !== undefined) d.自动卖装备栏 = parseEnabled(raw.自动卖装备栏)
  if (raw.自动卖其他栏 !== undefined) d.自动卖其他栏 = parseEnabled(raw.自动卖其他栏)
  if (raw.自动卖消耗品 !== undefined) d.自动卖消耗品 = parseEnabled(raw.自动卖消耗品)
  if (raw.统计金币 !== undefined) {
    d.统计金币 = parseEnabled(raw.统计金币)
  } else if (raw.自动识别金币 !== undefined) {
    d.统计金币 = parseEnabled(raw.自动识别金币)
  }
  if (raw.死亡自动回图 !== undefined) d.死亡自动回图 = parseEnabled(raw.死亡自动回图)
  if (raw.掉线重登录 !== undefined) d.掉线重登录 = parseEnabled(raw.掉线重登录)
  if (raw.脚本自动更新最高版本 !== undefined) {
    d.脚本自动更新最高版本 = parseEnabled(raw.脚本自动更新最高版本)
  }
  if (raw.自动代理 !== undefined) d.自动代理 = parseEnabled(raw.自动代理)
  if (PROXY_TYPES.includes(raw.代理类型)) d.代理类型 = raw.代理类型
  if (raw.定时离线 && typeof raw.定时离线 === 'object') {
    const t = raw.定时离线
    if (t.启用 !== undefined) d.定时离线.启用 = parseEnabled(t.启用)
    d.定时离线.开始小时 = clampHour(t.开始小时)
    d.定时离线.结束小时 = clampHour(t.结束小时)
  }
  if (JOB_OPTIONS.includes(raw.职业)) d.职业 = raw.职业
  return d
}

function serializeAdventureExtra(src) {
  return {
    自动杂货铺: !!src.自动杂货铺,
    自动买药: !!src.自动买药,
    自动买药类型: MEDICINE_TYPES.includes(src.自动买药类型) ? src.自动买药类型 : '白药水',
    自动买药间隔分钟: clampMedicineBuyIntervalMin(src.自动买药间隔分钟),
    自动卖装备栏: !!src.自动卖装备栏,
    自动卖其他栏: !!src.自动卖其他栏,
    自动卖消耗品: !!src.自动卖消耗品,
    统计金币: !!src.统计金币,
    死亡自动回图: !!src.死亡自动回图,
    掉线重登录: !!src.掉线重登录,
    脚本自动更新最高版本: !!src.脚本自动更新最高版本,
    自动代理: !!src.自动代理,
    代理类型: PROXY_TYPES.includes(src.代理类型) ? src.代理类型 : 'kit',
    定时离线: {
      启用: !!src.定时离线.启用,
      开始小时: clampHour(src.定时离线.开始小时),
      结束小时: clampHour(src.定时离线.结束小时)
    },
    职业: JOB_OPTIONS.includes(src.职业) ? src.职业 : '阴阳师'
  }
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i)

function formatHour(h) {
  return `${String(clampHour(h)).padStart(2, '0')} 点`
}

export default {
  name: 'CreateConfigAdventureIsland',
  props: {
    id: {
      type: [String, Number],
      default: null
    },
    viewMode: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const router = useRouter()
    const saving = ref(false)
    const loading = ref(false)

    const isEditMode = computed(() => !!props.id)
    const isViewMode = computed(() => props.viewMode === true)

    const isDefaultConfig = ref(false)

    const configForm = ref({
      name: isEditMode.value ? '' : '配置001',
      description: ''
    })

    const selectedHangMap = ref('')
    const hangMapOptions = ref([])
    const skillKeys = SKILL_KEYS
    const skillIntervals = ref(defaultSkillIntervals())
    /** 编辑时保留 content 里其它字段 */
    const baseContent = ref({})
    const adventureExtra = ref(defaultAdventureExtra())

    const skillFieldsDisabled = computed(
      () => isViewMode.value || (isEditMode.value && isDefaultConfig.value)
    )

    const extraFieldsDisabled = computed(
      () => isViewMode.value || (isEditMode.value && isDefaultConfig.value)
    )

    const hangMapSupportsGrocery = computed(() =>
      isHangMapGrocerySupported(selectedHangMap.value)
    )

    const groceryMasterDisabled = computed(
      () => extraFieldsDisabled.value || !hangMapSupportsGrocery.value
    )

    const grocerySubDisabled = computed(
      () =>
        extraFieldsDisabled.value ||
        !adventureExtra.value.自动杂货铺 ||
        !hangMapSupportsGrocery.value
    )

    const onGroceryMasterClick = (e) => {
      if (extraFieldsDisabled.value) return
      if (!hangMapSupportsGrocery.value) {
        e.preventDefault()
        e.stopPropagation()
        ElMessage.warning(
          `当前挂机地图不支持自动杂货铺。已开放：${GROCERY_SUPPORTED_MAPS_LABEL}；请切换挂机地图或等待后续开放。`
        )
      }
    }

    watch(selectedHangMap, () => {
      clearGroceryChainIfMapUnsupported(selectedHangMap.value, adventureExtra.value)
    })

    const displayHangMapRows = computed(() => {
      const list = hangMapOptions.value.map((r) => ({ ...r }))
      const sel = selectedHangMap.value
      if (sel && !list.some((r) => r.optionValue === sel)) {
        list.unshift({ id: null, optionValue: sel })
      }
      return list
    })

    const hangMapFilterQuery = ref('')

    const setHangMapFilterQuery = (val) => {
      hangMapFilterQuery.value = typeof val === 'string' ? val : ''
    }

    const filteredHangMapRowsForSelect = computed(() => {
      const rows = displayHangMapRows.value
      const sel = selectedHangMap.value
      const q = hangMapFilterQuery.value
      if (!String(q).trim()) return rows
      let list = rows.filter((r) => hangMapSearchMatch(q, r.optionValue))
      const cur = sel ? rows.find((r) => r.optionValue === sel) : null
      if (cur && !list.some((r) => r.optionValue === sel)) {
        list = [cur, ...list]
      }
      return list
    })

    const onHangMapDropdownVisible = (visible) => {
      if (!visible) {
        hangMapFilterQuery.value = ''
      }
    }

    const loadHangMapOptions = async () => {
      try {
        const res = await getAdventureHangMapOptions(GAME_NAME)
        if (res.code === 200 && Array.isArray(res.data)) {
          hangMapOptions.value = res.data
        }
      } catch (error) {
        console.error('加载挂机地图选项失败:', error)
        ElMessage.warning(
          '挂机地图列表加载失败（请确认已执行 backend/sql/adventure_hang_map_options.sql）'
        )
      }
    }

    const loadConfigIfNeeded = async () => {
      if (!props.id) return

      loading.value = true
      try {
        const response = await getConfig(props.id)
        const config = response.data

        if (!config) {
          ElMessage.error('配置不存在')
          return
        }

        configForm.value.name = config.name || ''
        configForm.value.description = config.description || ''

        // 默认配置标记（沿用旧逻辑里对 description 的包含判断）
        isDefaultConfig.value = !!(config.description && config.description.includes('系统为新用户自动创建的默认游戏配置'))

        const content = config.content || {}
        if (typeof content === 'object') {
          baseContent.value = { ...content }
          if (content['挂机地图']) {
            selectedHangMap.value = content['挂机地图']
          }
          skillIntervals.value = parseLoadedSkillIntervals(content['自动释放技能'])
          adventureExtra.value = parseAdventureExtra(content)
          clearGroceryChainIfMapUnsupported(selectedHangMap.value, adventureExtra.value)
        } else {
          baseContent.value = {}
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        ElMessage.error('加载配置失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      } finally {
        loading.value = false
      }
    }

    const saveConfig = async () => {
      if (!configForm.value.name.trim()) {
        ElMessage.error('请输入配置名称')
        return
      }
      if (!selectedHangMap.value || !String(selectedHangMap.value).trim()) {
        ElMessage.error('请选择挂机地图（或先在数据库中维护 adventure_hang_map_options）')
        return
      }
      if (isViewMode.value) return
      if (isEditMode.value && isDefaultConfig.value) return

      if (adventureExtra.value.定时离线.启用) {
        const sh = clampHour(adventureExtra.value.定时离线.开始小时)
        const eh = clampHour(adventureExtra.value.定时离线.结束小时)
        if (sh > eh) {
          ElMessage.error('定时离线：开始小时不能大于结束小时（同一天内，不跨天）')
          return
        }
      }

      if (adventureExtra.value.自动买药) {
        const medMin = normalizeMedicineBuyIntervalMin(adventureExtra.value.自动买药间隔分钟)
        if (medMin == null) {
          ElMessage.error('自动买药：请填写买药定时间隔（整数分钟）')
          return
        }
        if (medMin < MED_BUY_INTERVAL_MIN || medMin > MED_BUY_INTERVAL_MAX) {
          ElMessage.error(
            `自动买药：买药定时须在 ${MED_BUY_INTERVAL_MIN}～${MED_BUY_INTERVAL_MAX} 分钟之间`
          )
          return
        }
      }

      for (const k of SKILL_KEYS) {
        const row = skillIntervals.value[k]
        if (!row.enabled) continue
        const minSec = normalizeSec(row.minSec)
        const maxSec = normalizeSec(row.maxSec)
        if (minSec == null || maxSec == null) {
          ElMessage.error(`${k} 键：请填写有效的秒数（整数）`)
          return
        }
        if (minSec < SEC_MIN || maxSec < SEC_MIN) {
          ElMessage.error(`${k} 键：间隔不能小于 ${SEC_MIN} 秒`)
          return
        }
        if (minSec > SEC_MAX || maxSec > SEC_MAX) {
          ElMessage.error(`${k} 键：间隔不能大于 ${SEC_MAX} 秒`)
          return
        }
        if (minSec < 0 || maxSec < 0) {
          ElMessage.error(`${k} 键：间隔不能为负数`)
          return
        }
        if (minSec > maxSec) {
          ElMessage.error(`${k} 键：最短间隔不能大于最长间隔`)
          return
        }
      }

      clearGroceryChainIfMapUnsupported(selectedHangMap.value, adventureExtra.value)

      saving.value = true
      try {
        const autoSkill = {}
        SKILL_KEYS.forEach((k) => {
          const row = skillIntervals.value[k]
          const minSec = normalizeSec(row.minSec) ?? DEFAULT_SKILL_MIN
          const maxSec = normalizeSec(row.maxSec) ?? DEFAULT_SKILL_MAX
          autoSkill[k] = {
            enabled: !!row.enabled,
            minSec,
            maxSec
          }
        })

        const mergedContent = {
          ...(typeof baseContent.value === 'object' ? baseContent.value : {}),
          挂机地图: selectedHangMap.value,
          ...serializeAdventureExtra(adventureExtra.value),
          自动释放技能: autoSkill
        }

        const configData = {
          name: configForm.value.name,
          description: configForm.value.description,
          game: GAME_NAME,
          content: mergedContent
        }

        const bindWindowHint =
          '请记得在「窗口设置」里给窗口绑定该配置，否则脚本不会使用到此配置。'
        if (isEditMode.value) {
          await updateConfig(props.id, configData)
          ElMessage.success({
            message: `配置更新成功。${bindWindowHint}`,
            duration: 7000,
            showClose: true
          })
        } else {
          await createConfig(configData)
          ElMessage.success({
            message: `配置创建成功。${bindWindowHint}`,
            duration: 7000,
            showClose: true
          })
        }
        router.push({ name: 'ScriptConfig' })
      } catch (error) {
        console.error('保存配置失败:', error)
        ElMessage.error('保存失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      } finally {
        saving.value = false
      }
    }

    onMounted(async () => {
      if (props.id) {
        await loadConfigIfNeeded()
      }
      await loadHangMapOptions()
      if (!props.id && !selectedHangMap.value && hangMapOptions.value.length > 0) {
        selectedHangMap.value = hangMapOptions.value[0].optionValue
      }
    })

    return {
      loading,
      saving,
      configForm,
      selectedHangMap,
      displayHangMapRows,
      filteredHangMapRowsForSelect,
      setHangMapFilterQuery,
      onHangMapDropdownVisible,
      skillKeys,
      skillIntervals,
      skillFieldsDisabled,
      adventureExtra,
      extraFieldsDisabled,
      grocerySubDisabled,
      groceryMasterDisabled,
      hangMapSupportsGrocery,
      grocerySupportedMapsLabel: GROCERY_SUPPORTED_MAPS_LABEL,
      onGroceryMasterClick,
      hourOptions: HOUR_OPTIONS,
      formatHour,
      isEditMode,
      isViewMode,
      isDefaultConfig,
      saveConfig,
      medBuyIntervalMin: MED_BUY_INTERVAL_MIN,
      medBuyIntervalMax: MED_BUY_INTERVAL_MAX,
      medBuyIntervalDefault: MED_BUY_INTERVAL_DEFAULT
    }
  }
}
</script>

<style scoped>
.config-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 16px 18px;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.page-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 12px;
}

.config-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 12px 14px 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.config-form {
  padding: 2px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-item--tight {
  margin-top: 2px;
  margin-bottom: 0;
}

.form-item--grocery {
  gap: 6px;
}

.form-item--grocery-unsupported-map .grocery-master-row .form-check {
  cursor: not-allowed;
}

.grocery-master-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.grocery-master-label {
  font-weight: 600;
  color: #1e293b;
}

.form-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  vertical-align: middle;
}

.grocery-sub {
  margin-top: 4px;
  padding: 10px 10px 8px;
  border-radius: 10px;
  background: #fafbfc;
  border: 1px solid #eef2f7;
  transition: opacity 0.2s ease;
}

.grocery-sub--locked {
  opacity: 0.52;
}

.grocery-sub-hint {
  margin-top: 4px;
}

.check-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
}

.check-row--buy {
  gap: 12px;
}

.check-row--3 {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 10px;
  align-items: center;
}

.check-row--script-auto-update {
  margin-top: 6px;
}

.check-row--script-auto-update .form-check--cell {
  flex: 1;
  min-width: 0;
}

.form-check--cell {
  margin: 0;
  padding: 6px 8px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e8edf3;
  min-height: 36px;
  box-sizing: border-box;
}

.form-check--cell span {
  font-size: 12px;
}

.native-select--medicine {
  flex: 0 1 auto;
}

.medicine-interval-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 8px;
  padding-left: 2px;
}

.medicine-interval-row--one-line {
  flex-wrap: nowrap;
}

.medicine-interval-row--standalone {
  margin-top: 10px;
  padding-left: 2px;
}

.medicine-interval-label {
  font-size: 13px;
  color: #374151;
}

.medicine-interval-input {
  min-width: 0;
}

.medicine-interval-unit {
  font-size: 13px;
  color: #64748b;
}

.medicine-interval-hint-inline {
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  color: #6b7280;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 560px) {
  .check-row--3 {
    grid-template-columns: 1fr;
  }

  .check-row--buy {
    flex-direction: column;
    align-items: stretch;
  }

  .native-select.native-select--medicine {
    max-width: none;
    width: 100%;
  }

  .form-item--hang-map {
    flex-direction: column;
    align-items: stretch;
  }

  .form-item--hang-map .hang-map-field-body {
    width: 100%;
    max-width: none;
  }

  .medicine-interval-row--one-line {
    flex-wrap: wrap;
  }

  .medicine-interval-hint-inline {
    flex-basis: 100%;
    white-space: normal;
    overflow: visible;
  }

  .offline-one-line {
    flex-direction: column;
    align-items: stretch;
  }

  .offline-one-line .offline-one-line__seg {
    margin-top: 2px;
  }

  .offline-one-line .native-select.native-select--hour {
    width: 100%;
    max-width: none;
  }

  .form-item--job-row {
    flex-direction: column;
    align-items: stretch;
  }

  .form-item--job-row .native-select.native-select--job-inline {
    max-width: none;
    width: 100%;
  }
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-item--one-line {
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.form-item--job-row {
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.form-label--inline {
  margin: 0;
  flex-shrink: 0;
}

.native-select--job-inline {
  flex: 0 0 auto;
}

.form-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.form-check-stack {
  gap: 8px;
}

.form-check-stack .form-check + .form-check {
  margin-top: 2px;
}

.native-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
  cursor: pointer;
}

.native-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.form-hint {
  margin: 2px 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: #6b7280;
}

.form-hint a {
  color: #2563eb;
  text-decoration: none;
}

.form-hint a:hover {
  text-decoration: underline;
}

.form-hint--warn {
  color: #b45309;
}

.form-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
}

.native-select--inline {
  flex: 0 0 auto;
}

.native-select--hour {
  flex: 0 0 auto;
}

.offline-one-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  width: 100%;
}

.offline-one-line__seg {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.offline-hours-sep {
  font-size: 12px;
  color: #64748b;
}

.offline-hours-unit {
  font-size: 11px;
  color: #94a3b8;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.section-title {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: 0.02em;
  padding-top: 8px;
  border-top: 1px solid #eef2f7;
}

.skill-panel {
  margin-top: 6px;
  padding: 8px 10px 10px;
  border-radius: 10px;
  background: linear-gradient(165deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
}

.skill-panel--disabled {
  opacity: 0.92;
}

.skill-panel-desc {
  margin: 0 0 8px;
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
}

.skill-panel-desc strong {
  color: #475569;
  font-weight: 600;
}

.skill-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skill-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e8edf3;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.skill-line-key {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #1e40af;
  min-width: 2.25em;
}

.skill-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
}

.skill-switch--disabled {
  opacity: 0.65;
  cursor: not-allowed;
  pointer-events: none;
}

.skill-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}

.skill-switch-track {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.skill-switch-knob {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  top: 2px;
  left: 2px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
}

.skill-switch-input:checked + .skill-switch-track {
  background: #22c55e;
}

.skill-switch-input:checked + .skill-switch-track .skill-switch-knob {
  transform: translateX(18px);
}

.skill-switch-caption {
  font-size: 10px;
  font-weight: 800;
  color: #64748b;
  letter-spacing: 0.04em;
  min-width: 22px;
}

.skill-switch-input:checked ~ .skill-switch-caption {
  color: #15803d;
}

.skill-range-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding: 4px 5px;
  border-radius: 7px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}

.skill-num-input {
  flex: 1;
  min-width: 0;
  width: auto;
  height: 28px;
  border: 1px solid #d8dee9;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: center;
  padding: 0 4px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.skill-num-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2);
}

.skill-num-input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

.skill-range-tilde {
  flex-shrink: 0;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  user-select: none;
  padding: 0 1px;
}

.skill-range-suffix {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  min-width: 1.1em;
}

.native-input,
.native-select {
  width: 100%;
  height: 32px;
  border: 1px solid #d6dbe6;
  border-radius: 8px;
  background: #fff;
  color: #1f2937;
  font-size: 13px;
  padding: 0 10px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.native-select {
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #7a8699 50%),
    linear-gradient(135deg, #7a8699 50%, transparent 50%);
  background-position:
    calc(100% - 14px) calc(50% - 3px),
    calc(100% - 9px) calc(50% - 3px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

.native-input:focus,
.native-select:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.16);
}

.native-input:disabled,
.native-select:disabled {
  background: #f5f7fa;
  color: #9aa2af;
  cursor: not-allowed;
}

/* 覆盖 .native-input { width:100% }，买药分钟框只做短数字宽度 */
.native-input.medicine-interval-input {
  width: 64px;
  max-width: 72px;
  flex: 0 0 auto;
  padding: 0 8px;
  box-sizing: border-box;
  text-align: center;
}

/* 覆盖 .native-select { width:100% }，下拉按内容收窄（modifier 需写在全局规则之后） */
.native-select.native-select--hour {
  width: 5.75rem;
  min-width: 5.25rem;
  max-width: 6.25rem;
  flex: 0 0 auto;
  box-sizing: border-box;
  padding-left: 8px;
  padding-right: 22px;
}

.native-select.native-select--medicine {
  width: auto;
  min-width: 5.25rem;
  max-width: 7rem;
  flex: 0 1 auto;
}

.native-select.native-select--inline {
  width: auto;
  min-width: 3.25rem;
  max-width: 5.5rem;
  flex: 0 0 auto;
}

.native-select.native-select--job-inline {
  width: auto;
  min-width: 5rem;
  max-width: 7.5rem;
  flex: 0 0 auto;
}

.form-item--hang-map .hang-map-field-body {
  flex: 1;
  min-width: 12rem;
  max-width: min(52rem, 100%);
}

.hang-map-el-select {
  width: 100%;
  /* EP 用变量控制 input 字色，默认偏灰；改为实体深色 */
  --el-select-multiple-input-color: #111827;
}

.hang-map-el-select :deep(.el-select__wrapper) {
  min-height: 36px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #d6dbe6 inset;
  transition: box-shadow 0.2s ease;
}

.hang-map-el-select :deep(.el-select__wrapper:hover),
.hang-map-el-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #409eff inset;
}

/* 未选择：占位符灰色；已选择：filterable 会用 is-transparent 占位层展示选中项，默认过淡 */
.hang-map-el-select :deep(.el-select__placeholder:not(.is-transparent)) {
  color: #9ca3af;
}

.hang-map-el-select :deep(.el-select__placeholder.is-transparent) {
  color: #111827;
  opacity: 1;
}

.hang-map-el-select :deep(.el-select__wrapper) {
  color: #111827;
}

.hang-map-el-select :deep(.el-select__selection),
.hang-map-el-select :deep(.el-select__selected-item),
.hang-map-el-select :deep(.el-select__tags-text) {
  color: #111827;
}

.hang-map-el-select :deep(.el-select__input) {
  color: #111827 !important;
  -webkit-text-fill-color: #111827;
  caret-color: #111827;
  opacity: 1;
}

.hang-map-el-select :deep(.el-select__wrapper.is-disabled .el-select__input) {
  color: #9aa2af !important;
  -webkit-text-fill-color: #9aa2af;
  caret-color: #9aa2af;
}

.hang-map-el-select :deep(.el-select__wrapper.is-disabled) {
  color: #9aa2af;
}

.hang-map-el-select :deep(.el-select__wrapper.is-disabled .el-select__placeholder.is-transparent) {
  color: #9aa2af;
}

.hang-map-dropdown-empty {
  padding: 12px 14px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  text-align: center;
}

.bottom-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.primary-btn {
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.view-mode-hint {
  margin-top: 8px;
  color: #909399;
  font-size: 11px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .config-container {
    padding: 10px 10px 14px;
  }

  .page-head {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 17px;
  }

  .config-card {
    padding: 10px 12px 12px;
  }
}
</style>

<style>
/* 挂机地图下拉 teleported 到 body，需非 scoped */
.hang-map-select-popper.el-select__popper {
  border-radius: 10px;
}

.hang-map-select-popper .el-select-dropdown__wrap {
  max-height: min(320px, 46vh);
}

.hang-map-select-popper .el-select-dropdown__item {
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.45;
  white-space: normal;
  word-break: break-word;
  min-height: auto;
  height: auto;
}
</style>

